// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Tooltip } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { type ColumnMethods } from '@advisor-app/features/trades';
import { type TFunction } from 'i18next';
import {
  AppIcon,
  AssetBrand,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { type TradesHistory } from '@shared/data-access/interfaces/redux/trades';

const columnHelper = createColumnHelper<TradesHistory>();

export const renderColumnsTradesHistory = (
  _columnMethods: ColumnMethods,
  t: TFunction,
) => {
  const columns = [
    columnHelper.accessor('symbol', {
      header: t('common.ticker'),
      cell: ({ row }) => (
        <AssetBrand
          ticker={row.original.symbol}
          assetType={row.original.asset_type}
          url={row.original.url_morningstar}
          logoUrl={row.original.logo}
          watchlistButton={
            <WatchlistToggleButton
              ticker={row.original.symbol}
              assetType={row.original.asset_type}
            />
          }
        />
      ),
      size: 80,
    }),
    columnHelper.accessor(row => (row.isin === 'missing' ? null : row.isin), {
      header: t('common.isin'),
      cell: ({ getValue }) => (
        <span className="text-primary-medium">{getValue() ?? '-'}</span>
      ),
      size: 100,
    }),
    columnHelper.accessor(row => (row.name === 'missing' ? null : row.name), {
      header: t('common.company'),
      cell: ({ getValue }) => (
        <span className="text-primary-medium">{getValue() ?? '-'}</span>
      ),
      size: 360,
    }),
    columnHelper.accessor('amount', {
      header: t('common.volume'),
      meta: { alignment: 'end' },
      cell: ({ getValue }) => (
        <span className="!money-display text-primary-medium">
          {getValue() ? formatToMoney(getValue()) : '-'}
        </span>
      ),
      size: 80,
    }),
    columnHelper.accessor('date', {
      header: t('common.date'),
      cell: ({ getValue }) => (
        <Tooltip
          placement="top"
          title={Intl.DateTimeFormat(t('common.locale'), {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          }).format(getValue())}
        >
          <span className="text-primary-medium">
            {getValue()
              ? Intl.DateTimeFormat(t('common.locale')).format(getValue())
              : '-'}
          </span>
        </Tooltip>
      ),
      size: 80,
    }),
    columnHelper.display({
      header: '',
      id: 'options',
      size: 100,
      cell: ({ row }) => (
        <div className="flex justify-between">
          {row.original.side === 'BUY' && (
            <div className="mt-1.5">
              <AppIcon icon="Buy" size="xl" />
            </div>
          )}
          {row.original.side === 'SELL' && (
            <div className="mt-1.5">
              <AppIcon icon="Sell" size="xl" />
            </div>
          )}
        </div>
      ),
    }),
  ];

  return columns;
};
